// Admin SystemConfigPage — manage dropdown options (system_config) per category.
// Tabs are client-side; each tab's panel is keyed so it remounts with fresh state.
import { useEffect, useState } from "react";
import type { ComponentType } from "react";
import { useFetch, patchJson } from "../../hooks/useApi.js";
import { invalidateConfig } from "../../hooks/useConfig.js";
import ConfigTable from "../../components/admin/ConfigTable.js";
import AddOptionForm, { type AddOptionFormProps } from "../../components/admin/AddOptionForm.js";
import TutorEmailForm from "../../components/admin/TutorEmailForm.js";
import type { ConfigCategory, SystemConfig } from "@dm-lms/shared/types";

const TABS: { category: ConfigCategory; title: string }[] = [
  { category: "profession",     title: "Professions" },
  { category: "specialisation", title: "Specialisations" },
  { category: "course_mode",    title: "Course modes" },
  { category: "session_duration", title: "Session Durations" },
  { category: "session_venue",    title: "Session Venues" },
  { category: "payment_method", title: "Payment methods" },
  { category: "proof_type",    title: "Proof types" },
  { category: "mpesa_config",   title: "M-Pesa" },
  { category: "county",         title: "Counties" },
  { category: "tutor_email",    title: "Tutor emails" },
];

// Categories with their own add form; every other tab uses AddOptionForm.
const ADD_FORM_BY_CATEGORY: Partial<Record<ConfigCategory, ComponentType<AddOptionFormProps>>> = {
  tutor_email: TutorEmailForm,
};
// Categories whose label is not editable (tutor_email: the label is the address; remove and re-add).
const LABEL_LOCKED_CATEGORIES: readonly ConfigCategory[] = ["tutor_email"];

function CategoryPanel({ category, title }: { category: ConfigCategory; title: string }) {
  const { data, isLoading, error, refetch } = useFetch<SystemConfig[]>(`/api/config/all?category=${category}`);
  const [rows, setRows] = useState<SystemConfig[] | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  useEffect(() => {
    if (data) setRows(data);
  }, [data]);

  async function toggleActive(row: SystemConfig) {
    setSavingId(row.id);
    setToggleError(null);
    try {
      const res = await patchJson<SystemConfig>(`/api/config/${row.id}`, { is_active: !row.isActive });
      if (!res.ok) {
        setToggleError(res.error);
        return;
      }
      setRows((prev) => prev?.map((r) => (r.id === row.id ? { ...r, isActive: res.data.isActive } : r)) ?? prev);
      invalidateConfig(category);
    } catch {
      setToggleError("Unable to update option. Please try again.");
    } finally {
      setSavingId(null);
    }
  }

  // DeleteConfigOption has already called the API and invalidated the config cache.
  function handleDeleted(id: string) {
    setRows((prev) => prev?.filter((r) => r.id !== id) ?? prev);
  }

  // EditConfigLabel has already saved the label and invalidated the config cache.
  function handleLabelSaved(id: string, label: string) {
    setRows((prev) => prev?.map((r) => (r.id === id ? { ...r, label } : r)) ?? prev);
  }

  function handleAdded() {
    invalidateConfig(category);
    refetch();
  }

  const visibleRows = isLoading ? null : rows;
  const AddForm = ADD_FORM_BY_CATEGORY[category] ?? AddOptionForm;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-medium text-dm-navy">{title}</h2>
          {visibleRows && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-dm-slate">{visibleRows.length}</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => { setShowForm((shown) => !shown); }}
          className="rounded-md bg-dm-navy px-4 py-2 text-sm font-medium text-white transition hover:bg-dm-blue"
        >
          Add option
        </button>
      </div>

      {showForm && (
        <AddForm
          category={category}
          sortOrder={(rows?.length ?? 0) + 1}
          onAdded={handleAdded}
          onCancel={() => { setShowForm(false); }}
        />
      )}

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      {toggleError && <p className="mb-3 text-sm text-red-600">{toggleError}</p>}

      {!error && (
        <ConfigTable
          rows={visibleRows}
          savingId={savingId}
          onToggle={(row) => void toggleActive(row)}
          onDeleted={handleDeleted}
          onLabelSaved={handleLabelSaved}
          canEditLabel={!LABEL_LOCKED_CATEGORIES.includes(category)}
        />
      )}
    </div>
  );
}

export default function SystemConfigPage() {
  const [active, setActive] = useState<ConfigCategory>("profession");
  const activeTab = TABS.find((tab) => tab.category === active) ?? TABS[0];

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">System Config</h1>

      <div role="tablist" className="mb-6 flex flex-wrap gap-1 border-b border-gray-200">
        {TABS.map((tab) => (
          <button
            key={tab.category}
            type="button"
            role="tab"
            aria-selected={tab.category === active}
            onClick={() => { setActive(tab.category); }}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${
              tab.category === active
                ? "border-dm-navy text-dm-navy"
                : "border-transparent text-dm-slate hover:text-dm-navy"
            }`}
          >
            {tab.title}
          </button>
        ))}
      </div>

      {activeTab && <CategoryPanel key={activeTab.category} category={activeTab.category} title={activeTab.title} />}
    </div>
  );
}
